import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, fmtDate, fmtMoney } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, FunnelSimple, X, Check, Upload } from "@phosphor-icons/react";
import DeleteWorkflowButton from "@/components/DeleteWorkflowButton";
import ExportMenu from "@/components/ExportMenu";
import CaseImportModal from "@/components/CaseImportModal";
import TrackerUpdateModal from "@/components/TrackerUpdateModal";

const STATUS_BADGE = {
  open: "badge",
  in_progress: "badge badge-info",
  review: "badge badge-warn",
  closed: "badge badge-success",
};

export default function Cases() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tier = user?.ux_tier ?? 4;
  const allowBulk = tier >= 3;       // Department Heads + Executive only
  const showValueCol = tier >= 3;    // Field/Case-Mgr tiers don't need USD valuations
  const [cases, setCases] = useState([]);
  const [clients, setClients] = useState([]);
  const [meta, setMeta] = useState({ case_codes: [], case_types: [], countries: [], case_status: [] });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [country, setCountry] = useState("");
  const [code, setCode] = useState("");
  const [caseType, setCaseType] = useState(searchParams.get("type") || "");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showTrackerUpdate, setShowTrackerUpdate] = useState(false);
  const isCEO = (user?.ux_tier || 0) >= 5;
  const isTier3Plus = (user?.ux_tier || 0) >= 3;

  const load = async () => {
    setLoading(true);
    const params = { q: q || undefined, status: status || undefined, country: country || undefined, code: code || undefined, case_type: caseType || undefined };
    const { data } = await api.get("/cases", { params });
    setCases(data);
    setLoading(false);
  };

  useEffect(() => {
    api.get("/meta").then(({ data }) => setMeta(data));
    api.get("/clients").then(({ data }) => setClients(data));
    load();
    // eslint-disable-next-line
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, country, code, caseType]);
  // Keep ?type=... URL param in sync (so sidebar links highlight correctly).
  useEffect(() => {
    const cur = searchParams.get("type") || "";
    if (caseType !== cur) {
      const next = new URLSearchParams(searchParams);
      if (caseType) next.set("type", caseType); else next.delete("type");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line
  }, [caseType]);
  // React to navigation (e.g. sidebar click → ?type=Counterfeit)
  useEffect(() => {
    setCaseType(searchParams.get("type") || "");
    // eslint-disable-next-line
  }, [searchParams.toString()]);

  const toggle = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(s => s.size === cases.length ? new Set() : new Set(cases.map(c => c.case_id)));

  const bulk = async (newStatus) => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      await api.post("/cases/bulk", { case_ids: Array.from(selected), status: newStatus });
      setSelected(new Set());
      await load();
    } finally { setBulkBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="cases-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Case Management</div>
          <h1 className="font-display text-[28px] font-bold tracking-tight mt-1">Cases · {cases.length}</h1>
          <div className="text-[12px] text-[var(--span-text-2)] mt-1">CODE-COUNTRY-YEAR-SEQUENCE — 13 case types supported.</div>
        </div>
        <div className="flex items-center gap-2">
          <ExportMenu resource="cases" filename="span_cases" />
          {isTier3Plus && (
            <button
              data-testid="tracker-update-btn"
              onClick={() => setShowTrackerUpdate(true)}
              className="span-btn span-btn-ghost inline-flex items-center gap-1.5"
              title="Bulk update status/stage/next-action for existing cases from a tracker spreadsheet (tier 3+)"
            >
              <Upload size={14} /> Update tracker
            </button>
          )}
          {isCEO && (
            <button
              data-testid="import-cases-btn"
              onClick={() => setShowImport(true)}
              className="span-btn span-btn-ghost inline-flex items-center gap-1.5"
              title="Bulk import legacy NFOR cases from Excel or CSV (CEO only)"
            >
              <Upload size={14} /> Import
            </button>
          )}
          <Link to="/cases/nfor" data-testid="nfor-link" className="span-btn span-btn-ghost" title="14-field National Field Operation Report">
            <Plus size={14}/> NFOR
          </Link>
          <button data-testid="new-case-btn" onClick={() => setOpen(true)} className="span-btn">
            <Plus size={14} weight="bold" /> New case
          </button>
        </div>
      </div>

      <div className="card-flat p-4 flex items-center gap-3 flex-wrap" data-testid="cases-filters">
        <div className="flex items-center gap-2 text-[12px] text-[var(--span-text-2)]"><FunnelSimple size={14} /> Filters</div>
        <select data-testid="filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className="span-input max-w-[160px] h-9 py-0">
          <option value="">All status</option>
          {meta.case_status.map(s => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
        </select>
        <select data-testid="filter-code" value={code} onChange={(e) => setCode(e.target.value)} className="span-input max-w-[140px] h-9 py-0">
          <option value="">All codes</option>
          {meta.case_codes.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="filter-country" value={country} onChange={(e) => setCountry(e.target.value)} className="span-input max-w-[140px] h-9 py-0">
          <option value="">All countries</option>
          {meta.countries.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="filter-case-type" value={caseType} onChange={(e) => setCaseType(e.target.value)} className="span-input max-w-[160px] h-9 py-0">
          <option value="">All case types</option>
          {(meta.case_types || []).map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <input
          data-testid="filter-q" value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Search case code / title…"
          className="span-input flex-1 min-w-[200px] h-9 py-0"
        />
        {(q || status || country || code || caseType) && (
          <button onClick={() => { setQ(""); setStatus(""); setCountry(""); setCode(""); setCaseType(""); }} className="span-btn span-btn-ghost h-9">
            <X size={12} /> Reset
          </button>
        )}
      </div>

      {allowBulk && selected.size > 0 && (
        <div className="card-flat p-3 flex items-center justify-between" data-testid="bulk-bar" style={{ borderColor: "var(--span-text)" }}>
          <div className="text-[12px] font-medium">
            <span className="font-mono">{selected.size}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <button disabled={bulkBusy} data-testid="bulk-close" onClick={() => bulk("closed")} className="span-btn h-8 text-[11px]">Close cases</button>
            <button disabled={bulkBusy} onClick={() => bulk("in_progress")} className="span-btn span-btn-ghost h-8 text-[11px]">Mark in progress</button>
            <button onClick={() => setSelected(new Set())} className="span-btn span-btn-ghost h-8 text-[11px]"><X size={11}/> Clear</button>
          </div>
        </div>
      )}

      <div className="card-flat overflow-hidden">
        <table className="span-table" data-testid="cases-table">
          <thead>
            <tr>
              {allowBulk && (
                <th style={{ width: 32 }}>
                  <input data-testid="select-all" type="checkbox" checked={selected.size > 0 && selected.size === cases.length} onChange={toggleAll}/>
                </th>
              )}
              <th>Case code</th><th>Title</th><th>Client</th><th>Country</th><th>Priority</th><th>Status</th>
              {showValueCol && <th className="text-right">Value (USD)</th>}<th>Created</th>
              <th style={{ width: 50 }}></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={10} className="text-center py-8 text-[var(--span-text-2)] font-mono text-[11px]">LOADING…</td></tr>}
            {!loading && cases.length === 0 && <tr><td colSpan={10} className="text-center py-8 text-[var(--span-text-2)] text-[12px]">No cases match.</td></tr>}
            {cases.map((c) => (
              <tr key={c.case_id} className={c.deleted_at ? "opacity-60" : ""}>
                {allowBulk && (
                  <td><input data-testid={`select-${c.case_code}`} type="checkbox" checked={selected.has(c.case_id)} onChange={() => toggle(c.case_id)} /></td>
                )}
                <td>
                  <Link to={`/cases/${c.case_id}`} className="font-mono text-[12px] font-semibold hover:underline" data-testid={`case-link-${c.case_code}`}>{c.case_code}</Link>
                  {c.deleted_at && <span className="badge badge-danger ml-2 text-[9px]">deleted</span>}
                </td>
                <td className="max-w-[280px] truncate">{c.title}</td>
                <td className="text-[var(--span-text-2)]">{c.client_name}</td>
                <td><span className="badge">{c.country}</span></td>
                <td><span className={`badge ${c.priority === "high" ? "badge-danger" : c.priority === "low" ? "badge-success" : "badge-warn"}`}>{c.priority}</span></td>
                <td><span className={STATUS_BADGE[c.status] || "badge"}>{c.status.replace("_", " ")}</span></td>
                {showValueCol && <td className="text-right tnum font-mono">{fmtMoney(c.value_usd, "USD")}</td>}
                <td className="text-[var(--span-text-2)]">{fmtDate(c.created_at)}</td>
                <td className="text-right">
                  {!c.deleted_at && (
                    <DeleteWorkflowButton
                      entityType="case"
                      entityId={c.case_id}
                      entityLabel={c.case_code}
                      onRequested={load}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && <NewCaseModal clients={clients} meta={meta} onClose={() => setOpen(false)} onCreated={() => { setOpen(false); load(); }} />}
      {showImport && (
        <CaseImportModal
          onClose={() => setShowImport(false)}
          onImported={() => { load(); }}
        />
      )}
      {showTrackerUpdate && (
        <TrackerUpdateModal
          onClose={() => setShowTrackerUpdate(false)}
          onApplied={() => { load(); }}
        />
      )}
    </div>
  );
}

function NewCaseModal({ clients, meta, onClose, onCreated }) {
  const [form, setForm] = useState({
    code: "INV", country: "IN", client_id: clients[0]?.client_id || "", title: "", description: "",
    value_usd: 5000, priority: "medium", status: "open",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await api.post("/cases", { ...form, value_usd: Number(form.value_usd) }); onCreated(); }
    catch (e) { setErr(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="new-case-modal">
      <div className="card-flat w-full max-w-[560px]">
        <div className="px-6 py-4 border-b border-[var(--span-border)] flex items-center justify-between">
          <div>
            <div className="label-eyebrow">New Case</div>
            <div className="font-display font-bold text-[18px]">Create case file</div>
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label-eyebrow block mb-1.5">Case code</label>
              <select data-testid="new-case-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="span-input">
                {meta.case_codes.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="label-eyebrow block mb-1.5">Country</label>
              <select data-testid="new-case-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="span-input">
                {meta.countries.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="label-eyebrow block mb-1.5">Priority</label>
              <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="span-input">
                <option>low</option><option>medium</option><option>high</option>
              </select>
            </div>
          </div>
          <div>
            <label className="label-eyebrow block mb-1.5">Client</label>
            <select data-testid="new-case-client" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="span-input" required>
              <option value="">Select…</option>
              {clients.map(c => <option key={c.client_id} value={c.client_id}>{c.name} ({c.country_code})</option>)}
            </select>
          </div>
          <div>
            <label className="label-eyebrow block mb-1.5">Title</label>
            <input data-testid="new-case-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="span-input" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1.5">Description</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="span-input min-h-[80px]" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1.5">Estimated value (USD)</label>
            <input type="number" min="0" data-testid="new-case-value" value={form.value_usd} onChange={(e) => setForm({ ...form, value_usd: e.target.value })} className="span-input font-mono" />
            <div className="text-[11px] text-[var(--span-text-2)] mt-1 font-mono">{">"} 10,000 USD auto-escalates to CEO queue.</div>
          </div>
          {err && <div className="text-[12px] text-[var(--span-danger)]">{String(err)}</div>}
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="span-btn span-btn-ghost">Cancel</button>
            <button data-testid="submit-new-case" disabled={busy} className="span-btn disabled:opacity-50"><Check size={13}/> {busy ? "Creating…" : "Create case"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
